/**
 * 10-Vial Kits (TESTED + UNTESTED) — public catalog (/shop/10-vial).
 *
 * Card = owner-approved reference design (iter219):
 *   ┌─────────────────────────┐
 *   │ [BPC-157 pill]          │
 *   │                         │
 *   │     [CARTON IMAGE]      │   large, dominant, carton only
 *   │                         │
 *   │  BPC-157                │   name (bold)
 *   │  10mg x 10 vials        │   dosage (muted)
 *   │                         │
 *   │  $99.00        [+ ADD]  │   price (bold) · dark pill button right
 *   │  per 10-vial kit        │   subprice (muted)
 *   └─────────────────────────┘
 *
 * Stock: top-right pill shows available kits ("N Available", 0 when OOS); Add disabled when OOS.
 * Grid: 1 col mobile / 2 tablet / 4 desktop. Fonts: Manrope only.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Plus, ShieldCheck } from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

const MANROPE = 'Manrope, system-ui, sans-serif';

export default function Catalog10VK() {
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/products");
        if (cancelled) return;
        setProducts(Array.isArray(data) ? data : []);
        setError(false);
      } catch {
        if (cancelled) return;
        setError(true);
        setProducts((prev) => prev ?? []);
      }
    };
    load();
    // Keep the kit counter live: refresh on tab focus and every 30s.
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(load, 30000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, []);

  const tenVKProducts = useMemo(() => {
    if (!products) return null;
    return products
      .filter((p) => p.kit_vial_count === 10)
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "en", {
          sensitivity: "base",
          numeric: true,
        }),
      );
  }, [products]);

  return (
    <div
      className="min-h-screen"
      style={{ fontFamily: MANROPE, color: "#000000", background: "#F6F7F8" }}
      data-testid="catalog-10vk"
    >
      <style>{`
        @media (hover: hover) {
          .catalog10vk-add-btn:not(:disabled):hover {
            background: #333333 !important;
            transform: translateY(-2px);
          }
          .catalog10vk-coa-btn:not(:disabled):hover {
            background: #EAD8A8 !important;
          }
          .catalog10vk-add-btn:not(:disabled):active {
            transform: translateY(0);
          }
          .catalog10vk-card:hover {
            border-color: #8E949B !important;
            box-shadow: 0 4px 28px -6px rgba(0,0,0,0.22);
            transform: translateY(-3px);
          }
        }
      `}</style>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <header>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight leading-none"
            style={{ fontFamily: MANROPE, fontWeight: 700, color: "#000000" }}
            data-testid="catalog-10vk-title"
          >
            10-Vial Kits
          </h1>
          <p
            className="mt-4 text-[12px] sm:text-[13px] uppercase tracking-[0.24em]"
            style={{ fontFamily: MANROPE, fontWeight: 500, color: "rgba(0,0,0,0.70)" }}
            data-testid="catalog-10vk-subtitle"
          >
            10-Vial Research Kits
          </p>
        </header>

        <section className="mt-10 sm:mt-14">
          {tenVKProducts === null ? (
            <p
              className="text-[13px] uppercase tracking-[0.22em]"
              style={{ fontFamily: MANROPE, fontWeight: 500, color: "rgba(0,0,0,0.60)" }}
              data-testid="catalog-10vk-loading"
            >
              Loading…
            </p>
          ) : tenVKProducts.length === 0 ? (
            <p
              className="text-[13px] uppercase tracking-[0.22em]"
              style={{ fontFamily: MANROPE, fontWeight: 500, color: "rgba(0,0,0,0.60)" }}
              data-testid="catalog-10vk-empty"
            >
              {error ? "Unable to load kits." : "No kits available yet."}
            </p>
          ) : (
            [
              { key: "tested", label: "Tested Kits", items: tenVKProducts.filter((p) => p.testing_status === "tested") },
              { key: "untested", label: "Untested Kits", items: tenVKProducts.filter((p) => p.testing_status !== "tested") },
            ].filter((g) => g.items.length > 0).map((g, i) => (
              <div key={g.key} className={i > 0 ? "mt-16 sm:mt-20" : ""} data-testid={`catalog-10vk-section-${g.key}`}>
                <div className="mb-6 flex items-center gap-5">
                  <h2
                    className="uppercase tracking-[0.02em] leading-none whitespace-nowrap"
                    style={{
                      fontFamily: MANROPE,
                      fontWeight: 800,
                      fontSize: 22,
                      padding: "8px 14px",
                      borderRadius: 6,
                      border: `1px solid ${g.key === "tested" ? "#83B99A" : "#D4A55A"}`,
                      color: g.key === "tested" ? "#14532D" : "#7A4A06",
                      background: g.key === "tested" ? "#E7F4EA" : "#FCF1DA",
                    }}
                    data-testid={`catalog-10vk-section-title-${g.key}`}
                  >
                    {g.label}
                  </h2>
                  <span aria-hidden="true" className="flex-1" style={{ height: 1, background: "rgba(0,0,0,0.14)" }} data-testid={`catalog-10vk-section-divider-${g.key}`} />
                </div>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-7 lg:gap-8"
                  data-testid={`catalog-10vk-grid-${g.key}`}
                >
                  {g.items.map((p) => (
                    <ProductCard key={p.id || p.slug} product={p} />
                  ))}
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
}

/* ----------------------------- Helpers ----------------------------- */

/**
 * Pull a dosage from the product name. Returns the lowercase `Nmg` substring
 * (e.g. "10mg") or null if nothing parseable is present.
 */
function parseMgFromName(name) {
  if (!name) return null;
  const m = String(name).match(/(\d+(?:\.\d+)?)\s*mg\b/i);
  if (!m) return null;
  return `${m[1]}mg`;
}

/* ----------------------------- Product Card ----------------------------- */

function ProductCard({ product: p }) {
  const { add } = useCart();
  const [imgFailed, setImgFailed] = useState(false);
  const price = Number(p.price || 0);
  const priceStr = price.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const outOfStock = p.oos_override === true || Number(p.kits_in_stock ?? 0) <= 0;
  const isTested = (p.testing_status || "untested") === "tested";
  const availableKits = outOfStock ? 0 : Math.max(0, Math.floor(Number(p.kits_in_stock) || 0));
  const mg = parseMgFromName(p.name);
  const dosageLine = mg ? `${mg} x 10 vials` : "10 vials";

  const onAdd = (e) => {
    e.stopPropagation();
    if (outOfStock) return;
    if (add(p, 1) === false) return;
    toast.success(`${p.name} added`);
  };

  return (
    <article
      className="catalog10vk-card bg-white flex flex-col relative overflow-hidden"
      style={{
        border: "1px solid #C9CDD2",
        transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
        borderRadius: 15,
        fontFamily: MANROPE,
        color: "#000000",
        width: 371,
        height: 430,
        justifySelf: "start",
      }}
      data-testid={`catalog-10vk-card-${p.slug}`}
    >
      {/* Top row: product pill (left) + kit counter pill (right, mirrored inset) */}
      <div
        style={{
          paddingLeft: 23,
          paddingRight: 23,
          paddingTop: 17,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span
          className="inline-flex items-center justify-center"
          style={{
            fontFamily: MANROPE,
            fontWeight: 500,
            fontSize: 12,
            lineHeight: 1,
            color: "#111111",
            border: "1px solid rgba(0,0,0,0.16)",
            borderRadius: 999,
            height: 31,
            padding: "0 10px",
            background: "#FFFFFF",
            whiteSpace: "nowrap",
            width: "calc((100% - 16px) / 3)",
            flex: "0 0 auto",
            boxSizing: "border-box",
          }}
          data-testid={`catalog-10vk-pill-${p.slug}`}
        >
          <FitText text={p.name} max={12} />
        </span>
        <span
          className="inline-flex items-center justify-center"
          style={{
            fontFamily: MANROPE,
            fontWeight: 700,
            fontSize: 12,
            lineHeight: 1,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: isTested ? "#FFFFFF" : "#000000",
            background: isTested ? "#1E7B34" : "#FBBF2E",
            borderRadius: 999,
            height: 31,
            whiteSpace: "nowrap",
            width: "calc((100% - 16px) / 3)",
            flex: "0 0 auto",
            boxSizing: "border-box",
          }}
          data-testid={`catalog-10vk-testing-${p.slug}`}
        >
          {isTested ? "TESTED" : "UNTESTED"}
        </span>
        <span
          className="inline-flex items-center justify-center"
          style={{
            fontFamily: MANROPE,
            fontWeight: 700,
            fontSize: 12,
            lineHeight: 1,
            letterSpacing: "0.04em",
            color: "#000000",
            border: "1px solid rgba(0,0,0,0.22)",
            borderRadius: 999,
            height: 31,
            background: "#F2F2F2",
            whiteSpace: "nowrap",
            width: "calc((100% - 16px) / 3)",
            flex: "0 0 auto",
            boxSizing: "border-box",
          }}
          data-testid={`catalog-10vk-kits-${p.slug}`}
        >
          {availableKits} Available
        </span>
      </div>

      {/* 327×218 image drop-in box — product image (contain) or blank */}
      <div style={{ paddingLeft: 21, paddingRight: 21, paddingTop: 14 }}>
        <div
          style={{
            width: 327,
            height: 218,
            background: "#F5F5F5",
            border: "1px solid #E4E6E9",
            borderRadius: 12,
            overflow: "hidden",
          }}
          data-testid={`catalog-10vk-image-placeholder-${p.slug}`}
        >
          {p.image_url && !imgFailed && (
            <img
              src={p.image_url}
              alt={p.name}
              onError={() => setImgFailed(true)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "center",
                display: "block",
              }}
              data-testid={`catalog-10vk-image-${p.slug}`}
            />
          )}
        </div>
      </div>

      {/* Bottom content block — name/dosage/price/per-kit tight stack + ADD */}
      <div
        style={{ position: "relative", paddingLeft: 23, paddingRight: 21, paddingTop: 22, flex: 1 }}
      >
        <div style={{ minWidth: 0, paddingRight: 104 }}>
          <h2
            style={{
              fontFamily: MANROPE,
              fontWeight: 600,
              fontSize: 16,
              lineHeight: 1.15,
              color: "#000000",
              letterSpacing: "-0.005em",
              margin: 0,
            }}
            data-testid={`catalog-10vk-name-${p.slug}`}
          >
            {p.name}
          </h2>
          <p
            style={{
              fontFamily: MANROPE,
              fontWeight: 500,
              fontSize: 14,
              lineHeight: 1.2,
              color: "rgba(0,0,0,0.7)",
              marginTop: 11,
              marginBottom: 0,
            }}
            data-testid={`catalog-10vk-dosage-${p.slug}`}
          >
            {dosageLine}
          </p>
          <p
            style={{
              fontFamily: MANROPE,
              fontWeight: 700,
              fontSize: 24,
              lineHeight: 1,
              color: "#000000",
              letterSpacing: "-0.015em",
              marginTop: 22,
              marginBottom: 0,
            }}
            data-testid={`catalog-10vk-price-${p.slug}`}
          >
            ${priceStr}
          </p>
          <p
            style={{
              fontFamily: MANROPE,
              fontWeight: 500,
              fontSize: 12,
              lineHeight: 1.2,
              color: "rgba(0,0,0,0.7)",
              marginTop: 6,
              marginBottom: 0,
            }}
            data-testid={`catalog-10vk-per-${p.slug}`}
          >
            per 10-vial kit
          </p>
        </div>

        <Coa10VK product={p} />

        <button
          type="button"
          onClick={onAdd}
          disabled={outOfStock}
          className="catalog10vk-add-btn inline-flex items-center justify-center shrink-0 disabled:cursor-not-allowed"
          style={{
            position: "absolute",
            right: 22,
            bottom: 16,
            fontFamily: MANROPE,
            fontWeight: 600,
            fontSize: 13,
            letterSpacing: "0.02em",
            background: "#111111",
            color: "#FFFFFF",
            borderRadius: 999,
            width: 94,
            height: 44,
            padding: 0,
            border: "none",
            gap: 6,
            opacity: outOfStock ? 0.35 : 1,
            transition: "background-color 160ms ease, transform 160ms ease",
            cursor: outOfStock ? "not-allowed" : "pointer",
          }}
          aria-disabled={outOfStock ? "true" : undefined}
          data-testid={`catalog-10vk-add-${p.slug}`}
        >
          <Plus size={15} strokeWidth={2.4} />
          <span>ADD</span>
        </button>
      </div>
    </article>
  );
}

// Shrinks a single line of text until it fits its container (never truncates).
function FitText({ text, max = 12, min = 6 }) {
  const ref = useRef(null);
  const [size, setSize] = useState(max);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let s = max;
    el.style.fontSize = `${s}px`;
    while (s > min && el.scrollWidth > el.clientWidth) {
      s -= 0.5;
      el.style.fontSize = `${s}px`;
    }
    setSize(s);
  }, [text, max, min]);
  return (
    <span ref={ref} style={{ display: "block", width: "100%", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", fontSize: size }}>
      {text}
    </span>
  );
}

// COA button — same link behavior as the SELECT CoaBadge (Catalog.jsx): direct PDF in a
// new tab, hover chooser for multi-COA; greyed out when no COA is uploaded.
function Coa10VK({ product }) {
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const coas = product.coas || [];
  const directHref = (coa) => `${API}/api/products/${product.id}/coa/${coa.id}`;
  const legacyHref = `${API}/api/products/${product.id}/coa`;
  const stop = (e) => e.stopPropagation();
  const hasCoa = !!product.has_coa;
  const style = {
    position: "absolute",
    right: 22,
    top: 22,
    width: 94,
    height: 44,
    padding: 0,
    gap: 6,
    borderRadius: 999,
    fontFamily: MANROPE,
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: "0.02em",
    background: "#F3E6C4",
    border: "1px solid #D9C48F",
    color: "#3A2E12",
    textDecoration: "none",
    opacity: hasCoa ? 1 : 0.35,
    cursor: hasCoa ? "pointer" : "not-allowed",
    transition: "background-color 160ms ease",
  };
  const cls = "catalog10vk-coa-btn inline-flex items-center justify-center";
  const inner = (<><ShieldCheck size={15} strokeWidth={2.4} /><span>COA</span></>);
  const testid = `catalog-10vk-coa-${product.slug}`;
  if (!hasCoa) {
    return (
      <button type="button" disabled aria-disabled="true" className={cls} style={style} data-testid={testid} title="COA not yet available">
        {inner}
      </button>
    );
  }
  if (coas.length <= 1) {
    const href = coas.length === 1 ? directHref(coas[0]) : legacyHref;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={stop} className={cls} style={style} data-testid={testid} title="View Certificate of Analysis (opens in new tab)">
        {inner}
      </a>
    );
  }
  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button type="button" onClick={stop} className={cls} style={style} data-testid={testid} title="Choose a Certificate of Analysis">
          {inner}
        </button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="p-2 w-[260px] bg-white border-borderSubtle">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-charcoal/60 px-2 pt-1 pb-2">Choose certificate</div>
        <div className="flex flex-col gap-1">
          {coas.map((c) => (
            <a
              key={c.id}
              href={directHref(c)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stop}
              className="px-2 py-1.5 text-[12px] text-charcoal hover:bg-bone rounded-sm truncate"
              data-testid={`catalog-10vk-coa-choice-${product.slug}-${c.id}`}
              title={c.filename || `COA ${c.id}`}
            >
              {c.filename || `Certificate ${c.id}`}
            </a>
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
